'use client';
import React, { useCallback, useEffect, useState } from 'react';
import CharityGrid from '../../components/charities/CharityGrid';
import type { CharitySummary } from '../../components/charities/CharityCard';
import Footer from '../components/Footer';
import ThemeProvider from '../components/ThemeProvider';
import Skeleton from '../components/ui/Skeleton';
import { ScrollReveal, DividerRow, StaggerGroup, StaggerItem } from '../../components/shared';
import type { DividerRowItem } from '../../components/shared';
import {
  HandHeartIcon,
  SearchIcon,
  ShieldCheckIcon,
  GlobeIcon,
  AlertTriangleIcon,
} from '../../components/shared/icons';

const CATEGORY_FILTERS = ['Health', 'Education', 'Environment'];

/**
 * Charities page, polished to match the glass hero/stats-row treatment the
 * client mockup's CharitiesPage established. The mockup's own stats grid
 * invents numbers ("184 Verified Charities", "$12.7M+ Donations Tracked",
 * "15+ Supported Chains") - those are skipped here. The only number shown is
 * the real listing count from /api/charities; the other two stat cells are
 * qualitative trust statements (same pattern used on /donate's trust strip),
 * not fabricated metrics. All search/filter/pagination/seed logic below is
 * untouched - this only reskins the page chrome.
 */
export default function CharitiesPageClient() {
  const [list, setList] = useState<Record<string, unknown>[] | null>(null);
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize] = useState(24);
  const [total, setTotal] = useState<number | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [seeding, setSeeding] = useState(false);
  const [category, setCategory] = useState<string | null>(null);

  const load = useCallback(async () => {
    setErr(null);
    setList(null);
    try {
      const params = new URLSearchParams();
      if (q) params.set('q', q);
      if (category) params.set('category', category);
      params.set('page', String(page));
      params.set('pageSize', String(pageSize));
      const res = await fetch(`/api/charities?${params.toString()}`);
      if (!res.ok) throw new Error(`Status ${res.status}`);
      const js = (await res.json()) as unknown;
      if (Array.isArray(js)) {
        setList(js as Record<string, unknown>[]);
        setTotal(null);
      } else {
        const obj = js as Record<string, unknown>;
        setList((obj.results as Record<string, unknown>[]) || []);
        if (typeof obj.total === 'number') setTotal(obj.total as number);
      }
    } catch (e: unknown) {
      const errMsg = e && typeof e === 'object' && Object.prototype.hasOwnProperty.call(e, 'message') ? String((e as Record<string, unknown>)['message']) : String(e);
      setErr(errMsg);
      setList([]);
    }
  }, [q, page, category, pageSize]);

  useEffect(() => { load(); }, [load]);

  async function seedLocal() {
    if (!confirm('Import curated starter charities into the database? This is typically run once on a new environment.')) return;
    setSeeding(true);
    try {
      const r = await fetch('/api/charities/seed-local?allow=1', { method: 'POST' });
      if (!r.ok) throw new Error(`Seed failed: ${r.status}`);
      await load();
      alert('Seeding complete');
    } catch (e: unknown) {
      const msg = e && typeof e === 'object' && Object.prototype.hasOwnProperty.call(e, 'message') ? String((e as Record<string, unknown>)['message']) : String(e);
      alert('Seeding failed: ' + msg);
    }
    setSeeding(false);
  }

  // Real count straight off the API response - null while loading, never fabricated.
  const charityCount = total ?? (list ? list.length : null);

  const statItems: DividerRowItem[] = [
    {
      key: 'count',
      content: (
        <>
          <HandHeartIcon size={18} className="shrink-0" style={{ color: '#34d399' }} />
          <span className="min-w-0">
            <span className="block text-base sm:text-lg font-extrabold leading-tight text-emerald-50">
              {charityCount !== null ? charityCount : '—'}
            </span>
            <span className="block text-[11px] sm:text-xs font-medium leading-tight text-emerald-200/70">
              Charities Listed
            </span>
          </span>
        </>
      ),
    },
    {
      key: 'onchain',
      content: (
        <>
          <GlobeIcon size={18} className="shrink-0" style={{ color: '#2dd4bf' }} />
          <span className="min-w-0">
            <span className="block text-base sm:text-lg font-extrabold leading-tight text-emerald-50">On-Chain</span>
            <span className="block text-[11px] sm:text-xs font-medium leading-tight text-emerald-200/70">
              Direct Wallet Donations
            </span>
          </span>
        </>
      ),
    },
    {
      key: 'p2p',
      content: (
        <>
          <ShieldCheckIcon size={18} className="shrink-0" style={{ color: '#6ee7b7' }} />
          <span className="min-w-0">
            <span className="block text-base sm:text-lg font-extrabold leading-tight text-emerald-50">Peer-to-Peer</span>
            <span className="block text-[11px] sm:text-xs font-medium leading-tight text-emerald-200/70">
              No Middleman, Ever
            </span>
          </span>
        </>
      ),
    },
  ];

  return (
    <ThemeProvider>
      <div
        className="flex min-h-screen flex-col"
        style={{
          backgroundColor: '#020617',
          backgroundImage:
            'radial-gradient(circle at top, rgba(16,185,129,0.08), transparent 55%), radial-gradient(circle at bottom, rgba(13,148,136,0.06), transparent 55%)',
        }}
      >
        <main id="charities-page" className="flex-1 w-full flex flex-col items-center">
          <section aria-labelledby="charities-heading" className="page-section relative w-full pt-24 pb-5 sm:pt-28 sm:pb-6">
            <div className="mx-auto max-w-6xl">
              <ScrollReveal>
                <div
                  className="section-panel relative overflow-hidden rounded-[32px] border px-5 py-8 sm:px-10 sm:py-10"
                  style={{
                    // FIX: previous fill (rgba(6,30,24,...) -> rgba(2,8,8,...))
                    // was near-black, almost indistinguishable from the page
                    // background (#020617) - the whole hero card read as a
                    // bare outline instead of a surface. Lightened while
                    // keeping the emerald tint.
                    borderColor: 'rgba(16,185,129,0.32)',
                    background: 'linear-gradient(180deg, rgba(16,52,40,0.85), rgba(8,24,20,0.94))',
                    boxShadow:
                      '0 0 0 1px rgba(16,185,129,0.12) inset, 0 30px 70px -30px rgba(0,0,0,0.8), 0 0 60px -20px rgba(13,148,136,0.3)',
                  }}
                >
                  <div className="pointer-events-none absolute inset-0 -z-10" aria-hidden="true">
                    <div
                      className="absolute -top-24 left-1/3 h-72 w-72 rounded-full opacity-40 blur-3xl"
                      style={{ background: 'radial-gradient(circle, rgba(16,185,129,0.25), transparent 70%)' }}
                    />
                    <div
                      className="absolute bottom-0 right-0 h-72 w-72 rounded-full opacity-30 blur-3xl"
                      style={{ background: 'radial-gradient(circle, rgba(45,212,191,0.2), transparent 70%)' }}
                    />
                  </div>

                  <div className="relative z-10">
                    <span className="eyebrow-label">
                      <HandHeartIcon size={14} />
                      Verified &amp; On-Chain
                    </span>

                    <h1
                      id="charities-heading"
                      className="headline-balance mt-5 text-4xl sm:text-5xl font-extrabold tracking-tight leading-[1.02] bg-gradient-to-r from-emerald-300 via-teal-200 to-emerald-400 bg-clip-text text-transparent"
                    >
                      Verified Charities
                    </h1>
                    <p className="mt-4 max-w-2xl text-base sm:text-lg leading-relaxed text-emerald-100/80">
                      Discover audited nonprofits and donate directly on-chain. All donations are
                      peer-to-peer — you always control your wallet.
                    </p>

                    {/* Same fix as the profile hero: these were two long
                        `rounded-full` capsules in a wrapping flex row, so the
                        gap between them was whatever was left after they
                        nearly filled the row - near zero at most widths, so
                        they read as one blob. A grid gives each an exact half
                        and a fixed gutter at every width. */}
                    <div className="pr-highlight-grid mt-7 max-w-2xl">
                      <div className="pr-highlight">
                        <span className="pr-highlight-icon" aria-hidden="true">
                          <ShieldCheckIcon size={15} />
                        </span>
                        <span className="pr-highlight-body">
                          <span className="pr-highlight-eyebrow">Curated</span>
                          <span className="pr-highlight-text">Curated trust-first giving directory</span>
                        </span>
                      </div>
                      <div className="pr-highlight">
                        <span className="pr-highlight-icon" aria-hidden="true">
                          <SearchIcon size={15} />
                        </span>
                        <span className="pr-highlight-body">
                          <span className="pr-highlight-eyebrow">Low friction</span>
                          <span className="pr-highlight-text">
                            Search, filter, and open donation routes with less friction
                          </span>
                        </span>
                      </div>
                    </div>
                  </div>

                  <DividerRow
                    className="relative z-10 mt-8"
                    background="rgba(4,16,13,0.5)"
                    borderColor="rgba(16,185,129,0.18)"
                    cellClassName="flex flex-1 items-center min-w-0 gap-2.5 px-4 py-3 sm:px-4 sm:py-3.5"
                    items={statItems}
                  />
                </div>
              </ScrollReveal>
            </div>
          </section>

          <section className="page-section relative w-full pb-16">
            <div className="mx-auto max-w-6xl">
              <div
                className="section-panel rounded-[30px] border p-4 sm:p-5 mb-5"
                style={{ borderColor: 'rgba(16,185,129,0.28)', background: 'rgba(18,48,38,0.78)', backdropFilter: 'blur(18px)' }}
              >
                <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
                  <div className="max-w-2xl">
                    <p className="eyebrow-label">Directory Controls</p>
                    <p className="mt-3 text-sm leading-relaxed text-emerald-100/78">
                      Refine results first, then browse cards. Primary actions stay to the right on larger screens and stack cleanly on mobile.
                    </p>
                  </div>
                </div>

                {/* Search + actions. `.pr-actions` guarantees a gap on both
                    axes when these wrap, which is where the Refresh and
                    seed buttons previously ended up flush against each
                    other on narrow screens. */}
                <div className="mt-5 flex flex-col gap-3 md:flex-row md:items-center">
                  <div
                    className="flex min-h-[2.75rem] min-w-0 flex-1 items-center gap-2.5 rounded-xl border px-4 py-2.5"
                    style={{ borderColor: 'rgba(16,185,129,0.32)', background: 'rgba(3,12,9,0.55)' }}
                  >
                    <SearchIcon size={16} className="shrink-0" style={{ color: '#6ee7b7' }} />
                    <input
                      aria-label="Search charities"
                      placeholder="Search charities by name or category"
                      value={q}
                      onChange={(e) => setQ(e.target.value)}
                      className="min-w-0 flex-1 bg-transparent text-sm text-emerald-50 placeholder:text-emerald-500/60 focus:outline-none"
                    />
                  </div>
                  <div className="pr-actions">
                    <button type="button" className="pr-btn pr-btn-primary pr-btn-sm" onClick={() => load()}>
                      Refresh
                    </button>
                    {process.env.NODE_ENV !== 'production' ? (
                      <button
                        type="button"
                        className="pr-btn pr-btn-ghost pr-btn-sm"
                        onClick={() => seedLocal()}
                        disabled={seeding}
                      >
                        {seeding ? 'Seeding…' : 'Import starter charities'}
                      </button>
                    ) : null}
                  </div>
                </div>

                <div className="mt-5">
                  <div className="pr-label">Filter by category</div>
                  <div className="pr-actions">
                    <button
                      type="button"
                      onClick={() => { setCategory(null); setPage(1); }}
                      aria-pressed={!category}
                      className={`pr-chip ${!category ? 'pr-chip-on' : ''}`}
                    >
                      All
                    </button>
                    {CATEGORY_FILTERS.map((cat) => (
                      <button
                        key={cat}
                        type="button"
                        onClick={() => { setCategory(cat); setPage(1); }}
                        aria-pressed={category === cat}
                        className={`pr-chip ${category === cat ? 'pr-chip-on' : ''}`}
                      >
                        {cat}
                      </button>
                    ))}
                  </div>
                </div>

                {(q || category) && (
                  <div className="mt-5 flex flex-wrap items-center justify-between gap-3 border-t border-emerald-500/15 pt-4 text-[11px] text-emerald-100/80">
                    <div className="flex min-w-0 flex-wrap items-center gap-2">
                      <span className="font-semibold uppercase tracking-[0.14em] text-emerald-300/90">
                        Active filters
                      </span>
                      {q && (
                        <span className="max-w-full truncate rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2.5 py-1">
                          Search: {q}
                        </span>
                      )}
                      {category && (
                        <span className="rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2.5 py-1">
                          Category: {category}
                        </span>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => { setQ(''); setCategory(null); setPage(1); }}
                      className="pr-btn pr-btn-ghost pr-btn-sm"
                    >
                      Clear filters
                    </button>
                  </div>
                )}

                <div className="mt-5 rounded-xl border border-emerald-500/22 bg-emerald-950/35 px-4 py-3 text-sm text-emerald-100/78">
                  {charityCount !== null ? `Showing ${charityCount} charity option${charityCount === 1 ? '' : 's'}` : 'Loading charity listings'}
                  {category ? ` in ${category}` : ''}
                  {q ? ` matching “${q}”` : ''}.
                </div>
              </div>

              {err ? (
                <div
                  className="flex items-start gap-3 rounded-2xl border px-4 py-3.5 text-sm"
                  style={{ borderColor: 'rgba(248,113,113,0.35)', background: 'rgba(127,29,29,0.18)', color: '#fecaca' }}
                >
                  <AlertTriangleIcon size={18} className="mt-0.5 shrink-0" />
                  <span>Error loading charities: {err}</span>
                </div>
              ) : list === null ? (
                /* Skeletons mirror the real card's grid, padding, and
                   anatomy (logo + name, three description lines, footer
                   rule) so the layout doesn't jump when results land. The
                   old placeholders were a different shape and column count
                   entirely. */
                <div
                  className="mt-8 grid grid-cols-1 items-stretch gap-4 sm:grid-cols-2 sm:gap-5 lg:grid-cols-3"
                  aria-label="Loading charities"
                >
                  {Array.from({ length: 6 }).map((_, i) => (
                    <div key={i} className="pr-charity">
                      <div className="flex items-start gap-3">
                        <Skeleton className="h-12 w-12 shrink-0 rounded-[0.9rem] bg-emerald-500/10" />
                        <div className="min-w-0 flex-1">
                          <Skeleton className="mb-2 h-4 w-2/3 rounded bg-emerald-500/10" />
                          <Skeleton className="h-2.5 w-1/3 rounded bg-emerald-500/10" />
                        </div>
                      </div>
                      <div className="pr-charity-body mt-3.5">
                        <Skeleton className="mb-2 h-3 w-full rounded bg-emerald-500/10" />
                        <Skeleton className="mb-2 h-3 w-11/12 rounded bg-emerald-500/10" />
                        <Skeleton className="h-3 w-3/5 rounded bg-emerald-500/10" />
                      </div>
                      <div className="pr-charity-foot">
                        <Skeleton className="h-3 w-2/5 rounded bg-emerald-500/10" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : list.length === 0 ? (
                <div className="mt-8 flex flex-col items-center gap-3 rounded-[1.35rem] border border-dashed border-emerald-500/28 bg-emerald-950/30 px-6 py-14 text-center">
                  <HandHeartIcon size={22} style={{ color: '#6ee7b7' }} />
                  <p className="max-w-md text-sm text-emerald-100/80">
                    No charities found. You can seed sample data for development using the button above, or
                    connect a real database and seed from The Giving Block.
                  </p>
                </div>
              ) : (
                <StaggerGroup>
                  <StaggerItem>
                    <CharityGrid charities={list as unknown as CharitySummary[]} />
                  </StaggerItem>
                  {total && total > pageSize ? (
                    <div className="mt-7 flex flex-wrap items-center justify-between gap-3 border-t border-emerald-500/15 pt-5 text-xs text-emerald-100/80">
                      <div>
                        Page {page} of {Math.ceil(total / pageSize)}
                      </div>
                      <div className="pr-actions">
                        <button
                          type="button"
                          onClick={() => setPage((p) => Math.max(1, p - 1))}
                          disabled={page === 1}
                          className="pr-btn pr-btn-ghost pr-btn-sm"
                        >
                          Previous
                        </button>
                        <button
                          type="button"
                          onClick={() => setPage((p) => (total ? Math.min(Math.ceil(total / pageSize), p + 1) : p + 1))}
                          disabled={total ? page >= Math.ceil(total / pageSize) : false}
                          className="pr-btn pr-btn-ghost pr-btn-sm"
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  ) : null}
                </StaggerGroup>
              )}
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </ThemeProvider>
  );
}
